import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import api from "../utils/api";

const ENERGY_MATCHERS = ["energy", "usage", "power", "kwh", "kw"];
const USAGE_METRIC_KEY = "__usage__";
const USAGE_SERIES_CANDIDATES = ["kW", "power_kw", "TAP", "power", "kw"];

const defaultState = {
  rows: [],
  timeLabels: [],
  minValue: null,
  maxValue: null,
  avgValue: null,
};

const useHeatmapData = (targetMetricKey, dateRange) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rawData, setRawData] = useState(null);
  const abortControllerRef = useRef(null);
  const lastFetchParams = useRef({ start: null, end: null });

  const username = useMemo(() => localStorage.getItem("username") || "", []);

  const fetchHeatmap = useCallback(
    async (force = false) => {
      if (!username) {
        setError("Missing username. Please log in again.");
        return;
      }

      const start = dateRange?.start;
      const end = dateRange?.end;

      if (!start || !end) return;

      const startStr = formatDate(start);
      const endStr = formatDate(end);

      // Determine selected machine and metric from targetMetricKey
      let selectedMachinesParam = null;
      let metricParam = null;
      if (targetMetricKey) {
        if (String(targetMetricKey).toLowerCase().startsWith("usage")) {
          selectedMachinesParam = "Usage";
          metricParam = "power_kw"; // Usage is aggregated power
        } else if (String(targetMetricKey).includes("_")) {
          const parts = String(targetMetricKey).split("_");
          selectedMachinesParam = parts[0];
          metricParam = parts.slice(1).join("_");
        }
      }

      // Deduplication check (include selected machine + metric)
      if (
        !force &&
        lastFetchParams.current.start === startStr &&
        lastFetchParams.current.end === endStr &&
        lastFetchParams.current.selected === (selectedMachinesParam || null) &&
        lastFetchParams.current.metric === (metricParam || null)
      ) {
        return;
      }

      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setLoading(true);
        setError(null);
        setRawData(null); // Clear stale data

        const params = new URLSearchParams({
          username,
          mode: "hour",
          start: startStr,
          end: endStr,
        });

        if (selectedMachinesParam) params.append("selected_machines", selectedMachinesParam);
        if (metricParam) params.append("metric", metricParam);

        lastFetchParams.current = { start: startStr, end: endStr, selected: selectedMachinesParam || null, metric: metricParam || null };

        const response = await api.get(`/data?${params.toString()}`, {
          signal: abortControllerRef.current.signal,
        });
        const data = response.data?.data || response.data || null;
        setRawData(data);
      } catch (err) {
        if (
          err.name === "AbortError" ||
          err.message === "canceled" ||
          err.code === "ERR_CANCELED"
        ) {
          return;
        }
        console.error("Heatmap fetch error:", err);
        lastFetchParams.current = { start: null, end: null };
        setError(err.message || "Unable to load heatmap data");
      } finally {
        if (
          abortControllerRef.current &&
          !abortControllerRef.current.signal.aborted
        ) {
          setLoading(false);
        }
      }
    },
    [username, dateRange?.start, dateRange?.end],
  );

  useEffect(() => {
    fetchHeatmap();
  }, [fetchHeatmap]);

  const metricKey = useMemo(() => {
    if (!rawData) return null;

    if (
      targetMetricKey &&
      String(targetMetricKey).toLowerCase().startsWith("usage")
    ) {
      return USAGE_METRIC_KEY;
    }

    let keys = [];
    if (rawData.machines) {
      // Flatten keys for selection logic: "MachineName_Metric"
      Object.entries(rawData.machines).forEach(([id, machine]) => {
        const mName = machine.name || id;
        if (machine.data) {
          Object.keys(machine.data).forEach((metric) => {
            keys.push(`${mName}_${metric}`);
          });
        }
      });
    } else {
      keys = Object.keys(rawData).filter((key) => key !== "Date & Time");
    }

    if (!keys.length) return null;

    // Use provided key if it exists in data
    if (targetMetricKey && keys.includes(targetMetricKey))
      return targetMetricKey;

    const prioritized = keys.find((key) =>
      ENERGY_MATCHERS.some((matcher) => key.toLowerCase().includes(matcher)),
    );
    return prioritized || keys[0];
  }, [rawData, targetMetricKey]);

  const processed = useMemo(() => {
    if (!rawData || !metricKey) return defaultState;

    if (metricKey === USAGE_METRIC_KEY && rawData.machines) {
      const timestamps = normalizeArray(rawData["Date & Time"]);
      const gridMachines = Object.values(rawData.machines).filter(
        (machine) =>
          (machine?.category || "").trim().toUpperCase() === "GRID",
      );

      if (!timestamps.length || !gridMachines.length) return defaultState;

      const usageSeriesKey = resolveUsageSeriesKey(gridMachines);
      if (!usageSeriesKey) return defaultState;

      const values = timestamps.map((_, index) => {
        let foundValue = false;
        const total = gridMachines.reduce((sum, machine) => {
          const series = normalizeArray(machine?.data?.[usageSeriesKey]);
          const parsedValue = parseNumericValue(series[index]);
          if (parsedValue == null) return sum;
          foundValue = true;
          return sum + parsedValue;
        }, 0);
        return foundValue ? total : null;
      });

      const weekdayAverages = new Map();
      const WEEKDAYS = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ];

      WEEKDAYS.forEach((day) => {
        weekdayAverages.set(day, {
          label: day,
          values: {},
        });
      });

      timestamps.forEach((rawLabel, index) => {
        const parsed = parseTimestampRaw(rawLabel);
        if (!parsed) return;

        const { year, month, day, hours } = parsed;
        const date = new Date(year, parseInt(month, 10) - 1, parseInt(day, 10));
        let dayIdx = date.getDay();
        if (dayIdx === 0) dayIdx = 7;
        const dayName = WEEKDAYS[dayIdx - 1];

        const timeKey = `${hours}:00`;
        const value = values[index];

        if (value != null) {
          const bucket = weekdayAverages.get(dayName);
          if (!bucket.values[timeKey]) {
            bucket.values[timeKey] = { sum: 0, count: 0 };
          }
          bucket.values[timeKey].sum += value;
          bucket.values[timeKey].count += 1;
        }
      });

      const timeSet = new Set();
      const rows = WEEKDAYS.map((dayName) => {
        const bucket = weekdayAverages.get(dayName);
        const hourLabels = Array.from(
          { length: 24 },
          (_, i) => `${String(i).padStart(2, "0")}:00`,
        );
        hourLabels.forEach((t) => timeSet.add(t));

        return {
          label: dayName,
          values: hourLabels.map((timeKey) => {
            const stats = bucket.values[timeKey];
            return stats && stats.count > 0 ? stats.sum / stats.count : null;
          }),
        };
      });

      const timeLabels = Array.from(timeSet).sort();
      const allValues = rows
        .flatMap((row) => row.values)
        .filter((value) => value != null);
      const minValue = allValues.length ? Math.min(...allValues) : null;
      const maxValue = allValues.length ? Math.max(...allValues) : null;
      const avgValue = allValues.length
        ? allValues.reduce((total, current) => total + current, 0) /
          allValues.length
        : null;

      return {
        rows,
        timeLabels,
        minValue,
        maxValue,
        avgValue,
      };
    }

    // Adapt to New Structure for Flattened metrics
    let values = [];
    let timestamps = normalizeArray(rawData["Date & Time"]);

    if (rawData.machines) {
      // Find which machine and which metric this key refers to
      // Key format: "MachineName_Metric"
      const parts = metricKey.split("_");
      const machineName = parts[0];
      const metricName = parts.slice(1).join("_");

      const machineNode = Object.values(rawData.machines).find(
        (m) => m.name === machineName,
      );
      if (machineNode && machineNode.data && machineNode.data[metricName]) {
        values = normalizeArray(machineNode.data[metricName]).map(
          parseNumericValue,
        );
      }
    } else {
      // Legacy
      values = normalizeArray(rawData[metricKey]).map(parseNumericValue);
    }

    if (!values.length || !timestamps.length) return defaultState;

    const weekdayAverages = new Map();
    const WEEKDAYS = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    // Initialize map for all weekdays
    WEEKDAYS.forEach((day) => {
      weekdayAverages.set(day, {
        label: day,
        values: {}, // timeKey -> { sum: 0, count: 0 }
      });
    });

    timestamps.forEach((rawLabel, index) => {
      const parsed = parseTimestampRaw(rawLabel);
      if (!parsed) return;

      const { year, month, day, hours } = parsed;
      const date = new Date(year, parseInt(month, 10) - 1, parseInt(day, 10));
      let dayIdx = date.getDay(); // 0 is Sunday
      if (dayIdx === 0) dayIdx = 7;
      const dayName = WEEKDAYS[dayIdx - 1];

      const timeKey = `${hours}:00`;
      const value = values[index];

      if (value != null) {
        const bucket = weekdayAverages.get(dayName);
        if (!bucket.values[timeKey]) {
          bucket.values[timeKey] = { sum: 0, count: 0 };
        }
        bucket.values[timeKey].sum += value;
        bucket.values[timeKey].count += 1;
      }
    });

    const timeSet = new Set();
    const rows = WEEKDAYS.map((dayName) => {
      const bucket = weekdayAverages.get(dayName);
      // We want all hours 00:00 to 23:00 to be consistent
      const hourLabels = Array.from(
        { length: 24 },
        (_, i) => `${String(i).padStart(2, "0")}:00`,
      );
      hourLabels.forEach((t) => timeSet.add(t));

      return {
        label: dayName,
        values: hourLabels.map((timeKey) => {
          const stats = bucket.values[timeKey];
          return stats && stats.count > 0 ? stats.sum / stats.count : null;
        }),
      };
    });

    const timeLabels = Array.from(timeSet).sort();

    const allValues = rows
      .flatMap((row) => row.values)
      .filter((value) => value != null);
    const minValue = allValues.length ? Math.min(...allValues) : null;
    const maxValue = allValues.length ? Math.max(...allValues) : null;
    const avgValue = allValues.length
      ? allValues.reduce((total, current) => total + current, 0) /
        allValues.length
      : null;

    return {
      rows,
      timeLabels,
      minValue,
      maxValue,
      avgValue,
    };
  }, [rawData, metricKey]);

  return {
    loading,
    error,
    metricKey,
    refetch: fetchHeatmap,
    hasData: Boolean(processed.rows.length && processed.timeLabels.length),
    ...processed,
  };
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    return Object.values(value);
  }
  if (value == null) return [];
  return [value];
};

const resolveUsageSeriesKey = (gridMachines) => {
  for (const candidate of USAGE_SERIES_CANDIDATES) {
    const hasSeries = gridMachines.some((machine) => {
      const series = machine?.data?.[candidate];
      return Array.isArray(series) ? series.length > 0 : series != null;
    });
    if (hasSeries) return candidate;
  }

  const firstMachine = gridMachines[0];
  if (!firstMachine?.data) return null;

  return Object.keys(firstMachine.data).find((key) =>
    key.toLowerCase().includes("kw"),
  ) || null;
};

const parseNumericValue = (value) => {
  if (typeof value === "number") return value;
  if (value == null) return null;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Parse timestamp without timezone conversion - extracts components directly from string
const parseTimestampRaw = (rawLabel) => {
  if (!rawLabel) return null;
  const rawStr = String(rawLabel);
  // Support YYYY-MM-DD HH:mm:ss, YYYY/MM/DD, and T separator
  const match = rawStr.match(
    /(\d{4})[-/](\d{1,2})[-/](\d{1,2})[T\s](\d{1,2}):(\d{1,2})/,
  );
  if (!match) {
    // Try just date if time fails (though less useful for heatmap)
    const dateMatch = rawStr.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      return { year, month, day, hours: "00", minutes: "00" };
    }
    return null;
  }
  const [, year, month, day, hours, minutes] = match;
  return {
    year,
    month: month.padStart(2, "0"),
    day: day.padStart(2, "0"),
    hours: hours.padStart(2, "0"),
    minutes: minutes.padStart(2, "0"),
  };
};

const getRelativeRange = (days) => {
  const end = new Date();

  if (days === 3 || days === 7 || days === 30) {
    end.setDate(end.getDate() - 1);
  }

  end.setHours(23, 59, 59, 999);

  const start = new Date(end);
  start.setDate(end.getDate() - (days - 1));
  start.setHours(0, 0, 0, 0);

  return { start, end };
};

const formatDate = (date) => {
  if (!date || isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export default useHeatmapData;
